/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useState } from 'react';
import { customInspectionTypeSettings } from '../../../../common/src/domain/customInspectionTypes/customInspectionTypeSettings';
import { InspectionStandardTypes } from '../../../../common/src/enums/InspectionStandardTypes';
import { Roles } from '../../../../common/src/enums/Roles';
import { SurveyStates } from '../../../../common/src/enums/SurveyStates';
import { SurveyImportElementsAndUnitsNotFoundError } from '../../../../common/src/imports/surveyImport';
import { useTranslate } from '../../mixins/translate/translate';
import { assembleUrl } from '../../store/assembleUrl';
import { fetchGet, fetchJson } from '../../store/fetch';
import { appContext } from '../appContext';
import {
    Content,
    EditPanel,
    EditPanelCell,
    EditPanelFilterCreateSelect,
    EditPanelRow,
    EditPanelSection,
    EditPanelSelect,
    EditPanelSwitch,
    ISelectOption,
    Panel,
    PanelBody,
    PanelTable,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    TopbarCrumbActive,
    TopbarLeftOnly,
    Tray,
} from '../elements';
import { DropzoneComponent } from '../utils/dropzone/DropzoneComponent';
import { canImportFilter } from '../utils/inspectionStandardTypes/canImportFilter';
import { useGetInspectionStandardTypes } from '../utils/inspectionStandardTypes/useGetInspectionStandardTypes';
import { NullableBoolean } from '../utils/NullableBoolean';
import { SurveyImportErrorRow } from './SurveyImportErrorRow';

interface IUpload {
    filename: string;
    isSuccessful: boolean;
    message: string;
    error?: SurveyImportElementsAndUnitsNotFoundError | undefined;
}

type State = {
    uploads: IUpload[];
    clientCompanies: ISelectOption[];
    ownerCompanies: ISelectOption[];
    surveyorCompanies: ISelectOption[];
    knownLabels: string[];
    selectedLabels: string[];
    shouldOverrideLabels: boolean;
    clientCompanyId: undefined | string;
    ownerCompanyId: undefined | string;
    surveyorCompanyId: undefined | string;
    shouldOverrideSurveyorCompanyId: boolean;
    surveyStatus: undefined | string;
    shouldOverrideSurveyStatus: boolean;
    inspectionStandardType: string | undefined;
    inspectionStandardTypeOptions: ISelectOption[];
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export const ImportMultipleSurveys = () => {
    const translate = useTranslate();
    const currentAppContext = React.useContext(appContext.context);
    const [state, setState] = useState<State>({
        clientCompanies: [],
        ownerCompanies: [],
        surveyorCompanies: [],
        knownLabels: [],
        selectedLabels: [],
        uploads: [],
        clientCompanyId: undefined,
        ownerCompanyId: undefined,
        surveyorCompanyId: undefined,
        surveyStatus: undefined,
        inspectionStandardType: undefined,
        inspectionStandardTypeOptions: [],
        shouldOverrideLabels: false,
        shouldOverrideSurveyStatus: false,
        shouldOverrideSurveyorCompanyId: false,
    });

    const { inspectionStandardTypes } = useGetInspectionStandardTypes();

    function onUploadComplete(result: any) {
        if (result.xhr.status !== 200) {
            let errorMessage = '500';
            let error: any = undefined;
            try {
                const { responseText } = result.xhr;
                const response = JSON.parse(responseText);
                if (response.message) {
                    errorMessage = response.message;
                    if (response.message === 'SurveyImportElementsNotFoundError') {
                        const responseError = response.error as SurveyImportElementsAndUnitsNotFoundError;
                        error = new SurveyImportElementsAndUnitsNotFoundError(
                            responseError.sheet,
                            responseError.elements,
                            responseError.units,
                            responseError.objectsWithComposition,
                        );
                    }
                }
            } catch {}
            setState((prevState) => ({
                ...prevState,
                uploads: [...prevState.uploads, { filename: result.name, isSuccessful: false, message: errorMessage, error }],
            }));
        } else {
            const { responseText } = result.xhr;
            const response = JSON.parse(responseText);
            setState((prevState) => ({
                ...prevState,
                uploads: [...prevState.uploads, { filename: result.name, isSuccessful: response.isSuccessful, message: response.message }],
            }));
        }
    }

    useEffect(() => {
        const inspectionStandardTypesToImport = inspectionStandardTypes.filter(canImportFilter);
        const options = inspectionStandardTypesToImport.map((i) => ({ value: i, text: translate(`inspectionStandard.${i}`) }));
        setState((prevState) => ({ ...prevState, inspectionStandardTypeOptions: options }));
    }, [inspectionStandardTypes, translate]);

    React.useEffect(() => {
        (async () => {
            const result = await fetchGet('/api/surveys/knownLabels');
            setState((prevState) => ({ ...prevState, knownLabels: result?.json }));
        })();
    }, []);

    React.useEffect(() => {
        (async () => {
            const responseClientCompanies = await fetchJson('/api/clients', 'GET');
            setState((prevState) => ({
                ...prevState,
                clientCompanies: responseClientCompanies.json.items.map((company: { id: string; name: string }) => ({
                    text: company.name,
                    value: company.id,
                })),
            }));
        })();
    }, [currentAppContext.languageCode]);

    React.useEffect(() => {
        if (state.clientCompanyId) {
            (async () => {
                const responseOwnerCompanies = await fetchJson(`/api/companyList?type=${Roles.Owner}&companyid=${state.clientCompanyId}`, 'GET');
                const ownerCompanies =
                    responseOwnerCompanies.json?.items.map((company: { id: string; name: string }) => ({
                        text: company.name,
                        value: company.id,
                    })) ?? [];

                const responseSurveyorCompanies = await fetchJson(
                    `/api/companyList?type=${Roles.Surveyor}&companyid=${state.clientCompanyId}`,
                    'GET',
                );
                const surveyorCompanies =
                    responseSurveyorCompanies.json?.items.map((company: { id: string; name: string }) => ({
                        text: company.name,
                        value: company.id,
                    })) ?? [];
                setState((prevState) => ({ ...prevState, ownerCompanies, surveyorCompanies }));
            })();
        }
    }, [state.clientCompanyId]);

    const getDropzoneConfig = () => ({
        postUrl: assembleUrl('/api/survey/multipleImport', {
            labels: state.selectedLabels,
            shouldOverrideLabels: state.shouldOverrideLabels,
            clientCompanyId: state.clientCompanyId,
            ownerCompanyId: state.ownerCompanyId,
            surveyorCompanyId: state.surveyorCompanyId,
            shouldOverrideSurveyorCompanyId: state.shouldOverrideSurveyorCompanyId,
            surveyStatus: state.surveyStatus,
            shouldOverrideSurveyStatus: state.shouldOverrideSurveyStatus,
            inspectionStandardType: state.inspectionStandardType,
        }),
    });

    const isDropzoneVisible =
        !!state.inspectionStandardType &&
        !!state.clientCompanyId &&
        !!state.ownerCompanyId &&
        (!!state.surveyStatus || !state.shouldOverrideSurveyStatus);

    return (
        <>
            <TopbarLeftOnly>
                <TopbarCrumbActive title={translate('common.importItem', translate('common.surveys'))} icon="fa-upload" />
            </TopbarLeftOnly>
            <Content>
                <Tray>
                    <Panel>
                        <PanelBody>
                            <EditPanel>
                                <EditPanelRow>
                                    <EditPanelCell rowCount={6}>
                                        <EditPanelSelect
                                            name="clientCompanyId"
                                            placeholder={translate('common.selectItem', translate('common.client'))}
                                            value={state.clientCompanyId}
                                            options={state.clientCompanies}
                                            handleChange={(value) => setState((prevState) => ({ ...prevState, clientCompanyId: value }))}
                                        />
                                    </EditPanelCell>
                                </EditPanelRow>
                                <EditPanelRow>
                                    <EditPanelCell rowCount={6}>
                                        <EditPanelSelect
                                            name="inspectionStandardType"
                                            value={state.inspectionStandardType}
                                            handleChange={(value) => setState((prevState) => ({ ...prevState, inspectionStandardType: value }))}
                                            placeholder={translate('common.selectItem', translate('common.inspectionStandard'))}
                                            options={state.inspectionStandardTypeOptions}
                                        />
                                    </EditPanelCell>
                                </EditPanelRow>
                                {(state.inspectionStandardType === InspectionStandardTypes.Free ||
                                    state.inspectionStandardType === InspectionStandardTypes.Memo) && (
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={8}>
                                            This import will create new surveys and will not try to update existing surveys.
                                            <br />
                                            <a href="/assets/import-templates/SURVEY-IMPORT-TEMPLATE.xlsx" download>
                                                Template
                                            </a>
                                            <ul>
                                                <li>Object.Code is required.</li>
                                                {state.inspectionStandardType !== InspectionStandardTypes.Memo && (
                                                    <li>Object.Code should be unique.</li>
                                                )}
                                                <li>Survey.SurveyedOn is required.</li>
                                                {state.inspectionStandardType === InspectionStandardTypes.Memo && (
                                                    <li>Values in unknown column will be placed in the summary.</li>
                                                )}
                                            </ul>
                                        </EditPanelCell>
                                    </EditPanelRow>
                                )}

                                {state.inspectionStandardType === customInspectionTypeSettings.aoBasisgegevens!.inspectionStandardType && (
                                    <EditPanelRow>
                                        <EditPanelCell rowCount={8}>
                                            This import will create new surveys and will not try to update existing surveys.
                                            <br />
                                            <a href="/assets/import-templates/SURVEY-IMPORT-TEMPLATE-ELEMENTS-UNITS.xlsx" download>
                                                Template
                                            </a>
                                            <ul>
                                                <li>
                                                    Surveys:
                                                    <ul>
                                                        <li>Object.Code is required and should be unique.</li>
                                                        <li>Survey.SurveyedOn is required.</li>
                                                    </ul>
                                                </li>
                                                <li>
                                                    Elements:
                                                    <ul>
                                                        <li>Object.Code is required and should be unique.</li>
                                                        <li>Element.Code is required.</li>
                                                        <li>Element.Name is required.</li>
                                                        <li>Combination of Object.Code, Element.Code and Element.Name should be unique.</li>
                                                        <li>
                                                            Only use the letters and numbers of the name of the data fields, eg the field &quot;Use
                                                            data from county/muni.?&quot; needs the header
                                                            &quot;Element.Data.Use_data_from_countymuni&quot;
                                                        </li>
                                                    </ul>
                                                </li>
                                                <li>
                                                    Units:
                                                    <ul>
                                                        <li>Object.Code is required and should be unique.</li>
                                                        <li>Element.Code is required.</li>
                                                        <li>Element.Name is required.</li>
                                                        <li>Unit.Code is required.</li>
                                                        <li>Unit.Name is required.</li>
                                                        <li>
                                                            Combination of Object.Code, Element.Code, Element.Name, Unit.Code and Unit.Name should be
                                                            unique.
                                                        </li>
                                                        <li>
                                                            Only use the letters and numbers of the name of the data fields, eg the field &quot;Use
                                                            data from county/muni.?&quot; needs the header
                                                            &quot;Element.data.Use_data_from_countymuni&quot;
                                                        </li>
                                                    </ul>
                                                </li>
                                            </ul>
                                        </EditPanelCell>
                                    </EditPanelRow>
                                )}
                                {state.clientCompanyId && (
                                    <>
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={6}>
                                                <EditPanelSelect
                                                    name="ownerCompanyId"
                                                    placeholder={translate('common.selectItem', translate('common.owner'))}
                                                    value={state.ownerCompanyId}
                                                    options={state.ownerCompanies}
                                                    handleChange={(value) => setState((prevState) => ({ ...prevState, ownerCompanyId: value }))}
                                                />
                                            </EditPanelCell>
                                        </EditPanelRow>
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={8}>
                                                <EditPanelSwitch
                                                    name="shouldOverrideSurveyorCompanyId"
                                                    placeholder={translate('survey.surveyImportSheetOverride', translate('common.surveyor'))}
                                                    value={state.shouldOverrideSurveyorCompanyId}
                                                    handleChange={() =>
                                                        setState((prevState) => ({
                                                            ...prevState,
                                                            shouldOverrideSurveyorCompanyId: !prevState.shouldOverrideSurveyorCompanyId,
                                                        }))
                                                    }
                                                />
                                                <EditPanelSelect
                                                    name="surveyorCompanyId"
                                                    placeholder={translate('common.selectItem', translate('common.surveyor'))}
                                                    value={state.surveyorCompanyId}
                                                    options={state.surveyorCompanies}
                                                    handleChange={(value) => setState((prevState) => ({ ...prevState, surveyorCompanyId: value }))}
                                                    disabled={!state.shouldOverrideSurveyorCompanyId}
                                                />
                                            </EditPanelCell>
                                        </EditPanelRow>
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={6}>
                                                <EditPanelSwitch
                                                    name="shouldOverrideLabels"
                                                    placeholder={translate('survey.surveyImportSheetOverride', translate('common.labels'))}
                                                    value={state.shouldOverrideLabels}
                                                    handleChange={() =>
                                                        setState((prevState) => ({
                                                            ...prevState,
                                                            shouldOverrideLabels: !prevState.shouldOverrideLabels,
                                                        }))
                                                    }
                                                />
                                                <EditPanelFilterCreateSelect
                                                    name="labels"
                                                    value={state.selectedLabels}
                                                    placeholder={translate('common.selectItem', translate('common.labels'))}
                                                    options={state.knownLabels}
                                                    handleChange={(value) => setState((prevState) => ({ ...prevState, selectedLabels: value }))}
                                                    disabled={!state.shouldOverrideLabels}
                                                />
                                            </EditPanelCell>
                                            <EditPanelCell rowCount={6}>
                                                <EditPanelSwitch
                                                    name="shouldOverrideSurveyStatus"
                                                    placeholder={translate('survey.surveyImportSheetOverride', translate('common.status'))}
                                                    value={state.shouldOverrideSurveyStatus}
                                                    handleChange={() =>
                                                        setState((prevState) => ({
                                                            ...prevState,
                                                            shouldOverrideSurveyStatus: !prevState.shouldOverrideSurveyStatus,
                                                        }))
                                                    }
                                                />
                                                <EditPanelSelect
                                                    name="status"
                                                    placeholder={translate('common.selectItem', translate('common.status'))}
                                                    options={[
                                                        { value: SurveyStates.Open, text: translate(`surveyStatus.${SurveyStates.Open}`) },
                                                        {
                                                            value: SurveyStates.Preparation,
                                                            text: translate(`surveyStatus.${SurveyStates.Preparation}`),
                                                        },
                                                        {
                                                            value: SurveyStates.Conducting,
                                                            text: translate(`surveyStatus.${SurveyStates.Conducting}`),
                                                        },
                                                        { value: SurveyStates.Reporting, text: translate(`surveyStatus.${SurveyStates.Reporting}`) },
                                                        { value: SurveyStates.Delivery, text: translate(`surveyStatus.${SurveyStates.Delivery}`) },
                                                        { value: SurveyStates.Completed, text: translate(`surveyStatus.${SurveyStates.Completed}`) },
                                                    ]}
                                                    value={state.surveyStatus}
                                                    handleChange={(value) => setState((prevState) => ({ ...prevState, surveyStatus: value }))}
                                                    disabled={!state.shouldOverrideSurveyStatus}
                                                />
                                            </EditPanelCell>
                                        </EditPanelRow>
                                    </>
                                )}
                                {isDropzoneVisible && (
                                    <EditPanelSection>
                                        <div className="tray-bin p10 m5" style={{ minHeight: 250 }}>
                                            <h5 className="text-muted mt10 fw600 pl10">
                                                <i className="fa fa-exclamation-circle text-info fa-lg pr10" />
                                                {translate('survey.dragAndDropUploader')}{' '}
                                            </h5>
                                            <DropzoneComponent
                                                className="dropzone-sm"
                                                config={getDropzoneConfig()}
                                                djsConfig={{
                                                    parallelUploads: 1,
                                                }}
                                                eventHandlers={{
                                                    complete: onUploadComplete,
                                                }}
                                            />
                                        </div>
                                    </EditPanelSection>
                                )}
                                <PanelTable>
                                    <TableHead>
                                        <TableHeadRow>
                                            <TableHeader>Filename</TableHeader>
                                            <TableHeader>Successful</TableHeader>
                                            <TableHeader>Message</TableHeader>
                                        </TableHeadRow>
                                    </TableHead>
                                    <TableBody>
                                        {state.uploads.map((item, i) => (
                                            <TableRow key={i}>
                                                <TableData>{item.filename}</TableData>
                                                <TableData>
                                                    <NullableBoolean value={item.isSuccessful} />
                                                </TableData>
                                                <TableData>
                                                    <SurveyImportErrorRow
                                                        message={item.message}
                                                        error={item.error}
                                                        clientCompanyId={state.clientCompanyId}
                                                        ownerCompanyId={state.ownerCompanyId}
                                                    />
                                                </TableData>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </PanelTable>
                            </EditPanel>
                        </PanelBody>
                    </Panel>
                </Tray>
            </Content>
        </>
    );
};
